<template>
  <div class="investor-registration-page">
    <div class="container">
      <div class="registration-content">
        <router-link to="/" class="back-link">
          <i class="fa fa-arrow-left"></i>
          Back to Home
        </router-link>

        <div class="registration-card">
          <div class="card-header">
            <h2>Invest in a Franchise</h2>
            <p>Find the perfect franchise opportunity that matches your investment goals</p>
          </div>

          <div class="card-content">
            <form @submit.prevent="handleSubmit" class="investor-form">
              <div class="form-group">
                <label for="name">Full Name *</label>
                <input
                  id="name"
                  v-model="form.name"
                  type="text"
                  placeholder="Enter your full name"
                  required
                  @input="clearError('name')"
                  :class="{ 'error': errors.name }"
                />
                <span v-if="errors.name" class="error-message">{{ errors.name }}</span>
              </div>

              <div class="form-group">
                <label for="email">Email Address *</label>
                <input
                  id="email"
                  v-model="form.email"
                  type="email"
                  placeholder="Enter your email"
                  required
                  @input="clearError('email')"
                  :class="{ 'error': errors.email }"
                />
                <span v-if="errors.email" class="error-message">{{ errors.email }}</span>
              </div>

              <div class="form-group">
                <label for="phone">Phone Number *</label>
                <input
                  id="phone"
                  v-model="form.phone"
                  type="tel"
                  placeholder="Enter your phone number"
                  required
                  @input="clearError('phone')"
                  :class="{ 'error': errors.phone }"
                />
                <span v-if="errors.phone" class="error-message">{{ errors.phone }}</span>
              </div>

              <div class="form-group">
                <label for="location">Preferred Location *</label>
                <input
                  id="location"
                  v-model="form.location"
                  type="text"
                  placeholder="Enter your preferred location"
                  required
                  @input="clearError('location')"
                  :class="{ 'error': errors.location }"
                />
                <span v-if="errors.location" class="error-message">{{ errors.location }}</span>
              </div>

              <div class="form-group">
                <label for="investmentRange">Investment Range *</label>
                <select
                  id="investmentRange"
                  v-model="form.investmentRange"
                  required
                  @change="clearError('investmentRange')"
                  :class="{ 'error': errors.investmentRange }"
                >
                  <option value="">Select Investment Range</option>
                  <option value="0-5L">Below 5 Lakhs</option>
                  <option value="5-10L">5-10 Lakhs</option>
                  <option value="10-20L">10-20 Lakhs</option>
                  <option value="20-50L">20-50 Lakhs</option>
                  <option value="50L+">Above 50 Lakhs</option>
                </select>
                <span v-if="errors.investmentRange" class="error-message">{{ errors.investmentRange }}</span>
              </div>

              <div class="form-group">
                <label for="preferredIndustry">Preferred Industry *</label>
                <select
                  id="preferredIndustry"
                  v-model="form.preferredIndustry"
                  required
                  @change="clearError('preferredIndustry')"
                  :class="{ 'error': errors.preferredIndustry }"
                >
                  <option value="">Select Industry</option>
                  <option v-for="cat in categories" :key="cat.id" :value="cat.slug || cat.name">
                    {{ cat.name }}
                  </option>
                </select>
                <span v-if="errors.preferredIndustry" class="error-message">{{ errors.preferredIndustry }}</span>
              </div>

              <div class="form-group">
                <label for="experience">Business Experience</label>
                <textarea
                  id="experience"
                  v-model="form.experience"
                  rows="2"
                  placeholder="Tell us about your business experience (if any)"
                  @input="clearError('experience')"
                ></textarea>
              </div>

              <div class="form-actions">
                <router-link to="/" class="cancel-btn">Cancel</router-link>
                <button type="submit" :disabled="isSubmitting" class="submit-btn">
                  {{ isSubmitting ? 'Registering...' : 'Register as Investor' }}
                </button>
              </div>
            </form>
          </div>
        </div>

        <!-- Benefits Section -->
        <div class="benefits-section">
          <div class="benefits-grid">
            <div class="benefit-item">
              <div class="benefit-icon">
                <span>??</span>
              </div>
              <h3>Personalized Matches</h3>
              <p>Get franchise recommendations based on your criteria</p>
            </div>
            <div class="benefit-item">
              <div class="benefit-icon">
                <span>??</span>
              </div>
              <h3>Expert Guidance</h3>
              <p>Access to franchise consultants and industry experts</p>
            </div>
            <div class="benefit-item">
              <div class="benefit-icon">
                <span>??</span>
              </div>
              <h3>Market Insights</h3>
              <p>Detailed analytics and market research data</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import api from '@/utils/api'
import { useCategoryStore } from '@/stores/categoryStore'

const router = useRouter()
const categoryStore = useCategoryStore()

// Form data
const form = reactive({
  name: '',
  email: '',
  phone: '',
  location: '',
  investmentRange: '',
  preferredIndustry: '',
  experience: ''
})

// Form state
const errors = reactive({})
const isSubmitting = ref(false)

// Categories data - will be loaded from store
const categories = ref([])

// Methods
const validateForm = () => {
  const newErrors = {}
  
  if (!form.name.trim()) newErrors.name = 'Full name is required'
  if (!form.email.trim()) newErrors.email = 'Email is required'
  else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(form.email)) {
    newErrors.email = 'Please enter a valid email address'
  }
  
  if (!form.phone.trim()) newErrors.phone = 'Phone number is required'
  else if (!/^[0-9]{10}$/.test(form.phone.replace(/\s/g, ''))) {
    newErrors.phone = 'Please enter a valid 10-digit phone number'
  }
  
  if (!form.location.trim()) newErrors.location = 'Preferred location is required'
  if (!form.investmentRange) newErrors.investmentRange = 'Please select an investment range'
  if (!form.preferredIndustry) newErrors.preferredIndustry = 'Please select a preferred industry'
  
  Object.assign(errors, newErrors)
  return Object.keys(newErrors).length === 0
}

const clearError = (field) => {
  if (errors[field]) {
    delete errors[field]
  }
}

const handleSubmit = async () => {
  if (!validateForm()) return
  
  isSubmitting.value = true
  
  try {
    // Prepare the investor registration data
    const investorData = {
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      location: form.location.trim(),
      investment_range: form.investmentRange,
      preferred_industry: form.preferredIndustry,
      experience: form.experience.trim() || null,
      submitted_at: new Date().toISOString()
    }
    
    // Send data to backend using API service
    const response = await api.post('/investors', investorData)
    
    // Show success message
    const message = response.message || 'Registration submitted successfully! We will contact you soon.'
    alert(message)
    
    // Reset form
    Object.keys(form).forEach(key => {
      form[key] = ''
    })
    
    // Redirect to home
    router.push('/')
    
  } catch (error) {
    
    let errorMessage = 'Failed to submit registration. Please try again.'
    
    if (error.message.includes('Failed to fetch')) {
      errorMessage = 'Network error: Cannot connect to server. Please check your internet connection and try again.'
      
      // If we're in development or the API is not available, provide a mock success response
      if (window.location.hostname === 'localhost' || 
          window.location.hostname === '127.0.0.1' ||
          window.location.hostname.includes('localhost')) {
        

        
        // Show success message for development
        alert('Registration submitted successfully! (Mock response for development)')
        
        // Reset form
        Object.keys(form).forEach(key => {
          form[key] = ''
        })
        
        // Redirect to home
        router.push('/')
        return
      }
    } else if (error.response) {
      // Server responded with error status
      if (error.response.status === 422) {
        errorMessage = 'Please check your form data and try again.'
      } else if (error.response.status === 409) {
        errorMessage = 'An investor with this information already exists.'
      } else if (error.response.data?.message) {
        errorMessage = error.response.data.message
      }
    } else if (error.message) {
      errorMessage = error.message
    }
    
    alert(errorMessage)
  } finally {
    isSubmitting.value = false
  }
}

// Load categories on component mount
onMounted(async () => {
  try {
    // Fetch categories from store
    await categoryStore.fetchCategories()
    
    // Update local categories with store data
    categories.value = categoryStore.categories
    
  } catch (error) {
    
    // Fallback to static categories if API fails
    categories.value = [
      { id: 1, name: 'Food & Beverage', slug: 'food-beverage' },
      { id: 2, name: 'Retail', slug: 'retail' },
      { id: 3, name: 'Services', slug: 'services' },
      { id: 4, name: 'Health & Wellness', slug: 'health-wellness' },
      { id: 5, name: 'Education', slug: 'education' },
      { id: 6, name: 'Automobile', slug: 'automobile' }
    ]
  }
})
</script>

<style scoped>
.investor-registration-page {
  min-height: 100vh;
  background: linear-gradient(to bottom, #f9fafb, #ffffff);
  padding: 96px 0 64px;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px;
}

.registration-content {
  max-width: 1024px;
  margin: 0 auto;
}

.back-link {
  display: inline-flex;
  align-items: center;
  color: #6b7280;
  text-decoration: none;
  margin-bottom: 24px;
  font-weight: 500;
  transition: color 0.2s;
}

.back-link:hover {
  color: #2563eb;
}

.back-link i {
  margin-right: 8px;
}

/* Registration Card */
.registration-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  border: 1px solid #f3f4f6;
  margin-bottom: 48px;
}

.card-header {
  background: linear-gradient(to right, #2563eb, #1d4ed8);
  padding: 24px 32px;
  color: white;
}

.card-header h2 {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 8px;
}

.card-header p {
  color: #dbeafe;
  margin: 0;
}

.card-content {
  padding: 32px;
}

/* Form */
.investor-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 4px;
}

.form-group input,
.form-group select,
.form-group textarea {
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.2s;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.form-group input.error,
.form-group select.error {
  border-color: #dc2626;
}

.error-message {
  color: #dc2626;
  font-size: 12px;
  margin-top: 4px;
}

.form-group textarea {
  resize: vertical;
  min-height: 80px;
}

/* Form Actions */
.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  padding-top: 24px;
  border-top: 1px solid #f3f4f6;
}

.cancel-btn {
  padding: 12px 24px;
  border: 1px solid #d1d5db;
  color: #374151;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.2s;
}

.cancel-btn:hover {
  background: #f9fafb;
}

.submit-btn {
  padding: 12px 24px;
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
}

.submit-btn:hover:not(:disabled) {
  background: #1d4ed8;
}

.submit-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Benefits Section */
.benefits-section {
  margin-top: 48px;
}

.benefits-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 32px;
}

.benefit-item {
  text-align: center;
  padding: 24px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid #f3f4f6;
}

.benefit-icon {
  width: 48px;
  height: 48px;
  background: #dbeafe;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
}

.benefit-icon span {
  font-size: 20px;
}

.benefit-item h3 {
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 8px;
}

.benefit-item p {
  color: #6b7280;
  font-size: 14px;
  line-height: 1.6;
  margin: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .card-content {
    padding: 24px 16px;
  }
  
  .card-header {
    padding: 20px 16px;
  }
  
  .form-actions {
    flex-direction: column;
  }
  
  .benefits-grid {
    grid-template-columns: 1fr;
    gap: 24px;
  }
}

@media (max-width: 480px) {
  .card-content {
    padding: 16px;
  }
  
  .card-header {
    padding: 16px;
  }
  
  .card-header h2 {
    font-size: 20px;
  }
  
  .benefit-item {
    padding: 16px;
  }
}
</style>